import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/client';
import Seo from '../components/Seo';
import OtpLogin from '../components/common/OtpLogin';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { useUI } from '../context/UIContext';
import { money, formatDate } from '../utils/format';
import { STATUS_LABEL } from './Order';
import Spinner, { PageLoader } from '../components/common/Spinner';
import EmptyState from '../components/common/EmptyState';
import Image from '../components/common/Image';
import Price from '../components/product/Price';
import { useCart } from '../context/CartContext';
import { IconBag, IconHome, IconHeart, IconTrash } from '../components/common/Icons';

const EMPTY_ADDR = { label: 'Home', fullName: '', phone: '', line1: '', line2: '', city: '', state: '', postalCode: '', country: 'India', isDefault: false };

export default function Account() {
  const { user, loading, logout, updateMe, isAdmin } = useAuth();
  const { settings } = useSettings();
  const { toast } = useUI();
  const { wishlist, toggleWish } = useCart();
  const [tab, setTab] = useState('orders');
  const [orders, setOrders] = useState(null);
  const [profile, setProfile] = useState({ name: '', email: '' });
  const [addr, setAddr] = useState(null);
  const symbol = settings.shop?.currencySymbol || '$';

  useEffect(() => {
    if (user) {
      setProfile({ name: user.name || '', email: user.email || '' });
      api.get('/orders/mine').then((r) => setOrders(r.items)).catch(() => setOrders([]));
    }
  }, [user]);

  if (loading) return <PageLoader />;
  if (!user) {
    return (
      <>
        <Seo title="Account" noIndex />
        <div className="container-fluid section-my"><div className="max-w-md mx-auto eg-card"><OtpLogin /></div></div>
      </>
    );
  }

  const saveProfile = async (e) => {
    e.preventDefault();
    try {
      await updateMe(profile);
      toast('Profile updated');
    } catch (err) {
      toast(err.message, 'error');
    }
  };
  const saveAddress = async (e) => {
    e.preventDefault();
    const list = [...(user.addresses || [])];
    if (addr._id) {
      const i = list.findIndex((a) => a._id === addr._id);
      list[i] = addr;
    } else list.push(addr);
    if (addr.isDefault) list.forEach((a) => { a.isDefault = a === addr || a._id === addr._id; });
    try {
      await updateMe({ addresses: list });
      toast('Address saved');
      setAddr(null);
    } catch (err) {
      toast(err.message, 'error');
    }
  };
  const removeAddress = async (id) => {
    await updateMe({ addresses: user.addresses.filter((a) => a._id !== id) });
    toast('Address removed');
  };

  return (
    <>
      <Seo title="My Account" noIndex />
      <div className="container-fluid section-my">
        <div className="eg-account__head">
          <div><h1 className="text-3xl md:text-4xl">My Account</h1><p className="text-color-secondary">Hello {user.name || `+${user.phone}`}{isAdmin && <> · <Link to="/admin" className="underline">Admin panel</Link></>}</p></div>
          <button type="button" className="sf__btn sf__btn-secondary sf__btn-small" onClick={logout}>Log out</button>
        </div>
        <div className="eg-account">
          <aside>
            <ul className="eg-account-nav">
              {[['orders', 'Orders'], ['addresses', 'Addresses'], ['profile', 'Profile'], ['wishlist', 'Wishlist']].map(([k, l]) => (
                <li key={k}><button type="button" className={tab === k ? 'active' : ''} onClick={() => setTab(k)}>{l}</button></li>
              ))}
            </ul>
          </aside>
          <div className="eg-account__panel">
            {tab === 'orders' && (
              <div>
                <div className="eg-account-panel__head"><h2 className="text-xl font-medium">Order history</h2></div>
                {!orders ? <Spinner /> : orders.length === 0 ? (
                  <EmptyState
                    icon={<IconBag width={26} height={26} />}
                    title="No orders yet"
                    text="When you place an order it will appear here with its status and tracking details."
                    actionLabel="Start shopping"
                    actionTo="/collections/all"
                  />
                ) : (
                  <table className="eg-table w-full">
                    <thead><tr><th>Order</th><th>Date</th><th>Status</th><th>Payment</th><th className="text-right">Total</th></tr></thead>
                    <tbody>
                      {orders.map((o) => (
                        <tr key={o._id}>
                          <td><Link to={`/order/${o._id}`} className="underline font-medium">#{o.orderNumber}</Link></td>
                          <td>{formatDate(o.createdAt)}</td>
                          <td><span className={`eg-status eg-status--${o.status}`}>{STATUS_LABEL[o.status]}</span></td>
                          <td className="capitalize">{o.paymentStatus}</td>
                          <td className="text-right">{money(o.total, symbol)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
            {tab === 'addresses' && (
              <div>
                <div className="eg-account-panel__head">
                  <h2 className="text-xl font-medium">Addresses</h2>
                  {(user.addresses || []).length > 0 && <button type="button" className="sf__btn sf__btn-primary sf__btn-small" onClick={() => setAddr({ ...EMPTY_ADDR, fullName: user.name, phone: user.phone })}>Add address</button>}
                </div>
                {addr && (
                  <form onSubmit={saveAddress} className="eg-card mb-6 grid md:grid-cols-2 gap-3">
                    <input className="form-control" placeholder="Label (Home, Office)" value={addr.label} onChange={(e) => setAddr({ ...addr, label: e.target.value })} />
                    <input className="form-control" placeholder="Full name" required value={addr.fullName} onChange={(e) => setAddr({ ...addr, fullName: e.target.value })} />
                    <input className="form-control" placeholder="Phone" required value={addr.phone} onChange={(e) => setAddr({ ...addr, phone: e.target.value })} />
                    <input className="form-control" placeholder="Country" required value={addr.country} onChange={(e) => setAddr({ ...addr, country: e.target.value })} />
                    <input className="form-control md:col-span-2" placeholder="Address line 1" required value={addr.line1} onChange={(e) => setAddr({ ...addr, line1: e.target.value })} />
                    <input className="form-control md:col-span-2" placeholder="Address line 2" value={addr.line2} onChange={(e) => setAddr({ ...addr, line2: e.target.value })} />
                    <input className="form-control" placeholder="City" required value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} />
                    <input className="form-control" placeholder="State" value={addr.state} onChange={(e) => setAddr({ ...addr, state: e.target.value })} />
                    <input className="form-control" placeholder="Postal code" value={addr.postalCode} onChange={(e) => setAddr({ ...addr, postalCode: e.target.value })} />
                    <label className="flex items-center text-sm"><input type="checkbox" className="mr-2" checked={addr.isDefault} onChange={(e) => setAddr({ ...addr, isDefault: e.target.checked })} />Default address</label>
                    <div className="md:col-span-2 flex gap-2"><button type="submit" className="sf__btn sf__btn-primary sf__btn-small">Save</button><button type="button" className="sf__btn sf__btn-secondary sf__btn-small" onClick={() => setAddr(null)}>Cancel</button></div>
                  </form>
                )}
                <div className="grid md:grid-cols-2 gap-4">
                  {(user.addresses || []).map((a) => (
                    <div key={a._id} className="eg-card">
                      <div className="flex justify-between"><strong>{a.label}</strong>{a.isDefault && <span className="eg-badge">Default</span>}</div>
                      <p className="text-sm mt-2">{a.fullName}<br />{a.line1}{a.line2 ? `, ${a.line2}` : ''}<br />{[a.city, a.state, a.postalCode].filter(Boolean).join(', ')}<br />{a.country}<br />{a.phone}</p>
                      <div className="flex gap-3 mt-3 text-sm"><button type="button" className="underline" onClick={() => setAddr(a)}>Edit</button><button type="button" className="underline text-red-600" onClick={() => removeAddress(a._id)}>Delete</button></div>
                    </div>
                  ))}
                </div>
                {(user.addresses || []).length === 0 && !addr && (
                  <EmptyState
                    icon={<IconHome width={26} height={26} />}
                    title="No saved addresses"
                    text="Save an address to check out faster next time."
                    actionLabel="Add address"
                    onAction={() => setAddr({ ...EMPTY_ADDR, fullName: user.name, phone: user.phone })}
                  />
                )}
              </div>
            )}

            {tab === 'wishlist' && (
              <div>
                <div className="eg-account-panel__head"><h2 className="text-xl font-medium">Wishlist</h2></div>
                {wishlist.length === 0 ? (
                  <EmptyState
                    icon={<IconHeart width={26} height={26} />}
                    title="Your wishlist is empty"
                    text="Tap the heart on any product to save it here for later."
                    actionLabel="Browse products"
                    actionTo="/collections/all"
                  />
                ) : (
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-6">
                    {wishlist.map((p) => (
                      <div key={p._id} className="text-center relative">
                        <button type="button" className="absolute top-2 right-2 z-10 bg-white rounded-full p-2 shadow" onClick={() => toggleWish(p)} aria-label={`Remove ${p.title} from wishlist`}><IconTrash width={14} height={14} /></button>
                        <Link to={`/products/${p.slug}`}><Image src={p.image} alt={p.title} ratio={1} /></Link>
                        <h3 className="font-medium mt-3"><Link to={`/products/${p.slug}`}>{p.title}</Link></h3>
                        <Price price={p.price} compareAtPrice={p.compareAtPrice} className="justify-center" />
                        <Link to={`/products/${p.slug}`} className="sf__btn sf__btn-secondary sf__btn-small mt-3 inline-block">View product</Link>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {tab === 'profile' && (
              <form onSubmit={saveProfile} className="eg-card max-w-lg">
                <h2 className="text-xl font-medium mb-4">Profile details</h2>
                <label className="block text-sm mb-1">WhatsApp number</label>
                <input className="form-control w-full mb-3" value={`+${user.phone}`} disabled />
                <label className="block text-sm mb-1">Name</label>
                <input className="form-control w-full mb-3" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} />
                <label className="block text-sm mb-1">Email</label>
                <input className="form-control w-full mb-4" type="email" value={profile.email} onChange={(e) => setProfile({ ...profile, email: e.target.value })} />
                <button type="submit" className="sf__btn sf__btn-primary">Save changes</button>
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
